import { useState } from 'react';
import { Stack } from '@mui/material';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import {
  FixedStrikeTable,
  FixedStrikeProvider,
} from 'components/implied_vol/FixedStrikeMatrix';
import { FixedStrikeTableSettings } from 'components/implied_vol/FixedStrikeMatrix/FixedStrikeTableControls';
import { CanvasComponentConfig, CanvasContentId } from 'types/canvas';
import { DEFAULT_FIXED_STRIKE_CONFIG, FixedStrikeConfig } from 'types';
import { useRecoilValue } from 'recoil';
import { hiroSymbolsState } from 'states/hiro';
import CanvasSymSelector from './CanvasSymSelector';

interface CanvasFixedStrikeMatrixProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const CanvasFixedStrikeMatrix = ({
  id,
  sym,
  config: initialConfig,
  onSymChange,
  onConfigChange,
}: CanvasFixedStrikeMatrixProps) => {
  const hiroSymbols = useRecoilValue(hiroSymbolsState);
  const symOptions = Array.from(hiroSymbols)
    .sort()
    .map((s) => ({ symbol: s }));
  const [config, setConfig] = useState<FixedStrikeConfig>(() => ({
    ...DEFAULT_FIXED_STRIKE_CONFIG,
    ...(initialConfig as Partial<FixedStrikeConfig>),
    sym,
  }));

  const handleConfigChange = (patch: Partial<FixedStrikeConfig>) => {
    setConfig((prev) => {
      const next = { ...prev, ...patch };
      if (patch.sym != null && patch.sym !== prev.sym) {
        onSymChange(patch.sym);
      }
      return next;
    });
    // Sym is broadcast through handleComponentSingleSymChange separately;
    // every other key gets persisted through the standard config-change
    // path so the user's settings survive remounts.
    const { sym: _sym, ...rest } = patch;
    if (Object.keys(rest).length > 0) {
      onConfigChange(rest as Partial<CanvasComponentConfig>);
    }
  };

  const expandableContentButtonType = `${CanvasContentId.FIXED_STRIKE_MATRIX}-${id}`;

  return (
    <ExpandableContentWrapper
      type={expandableContentButtonType}
      styleOverrides={{ p: 0, height: '100%' }}
    >
      <FixedStrikeProvider config={config} onConfigChange={handleConfigChange}>
        <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1, py: 0.5, justifyContent: 'flex-end', width: '100%' }}
          >
            <CanvasSymSelector
              sym={config.sym}
              options={symOptions}
              onSymChange={(newSym) => handleConfigChange({ sym: newSym })}
            />
            <FixedStrikeTableSettings />
            <CanvasGroupLinkButton
              componentId={id}
              contentId={CanvasContentId.FIXED_STRIKE_MATRIX}
            />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <FixedStrikeTable />
        </Stack>
      </FixedStrikeProvider>
    </ExpandableContentWrapper>
  );
};

export default CanvasFixedStrikeMatrix;
